import { Dispatch, SetStateAction, useEffect, useState } from "react";

import { useSearch } from "@/utils";

import { LinkedinPersonSuggestionItem } from "@/components/LinkedinPersonSuggestion/LinkedinPersonSuggestion";

import {
	useLazyGetEnrichedCompanySuggestionQuery,
	useLazyGetEnrichedPersonSuggestionQuery,
	useLazyResolveEnrichedCompanyByUrlQuery,
	useLazyResolveEnrichedCompanySuggestionQuery,
	useLazyResolveEnrichedPersonByUrlQuery,
	useLazyResolveEnrichedPersonSuggestionQuery,
} from "@/pages/Private/redux/liPeople/liPeople.api";

import { EnrichedSuggestion, Suggestion } from "../../LiPeople/schema/liPeople";
import { LinkedinUrlType, classifyLinkedinUrl } from "../utils/linkedinUrl";

/** Labels for the single "resolve this pasted URL" option shown instead of search results. */
const URL_OPTION_TITLE: Record<LinkedinUrlType, string> = {
	PERSON: "Track this LinkedIn profile",
	COMPANY: "Track this LinkedIn company",
};

/** A pasted URL becomes one actionable suggestion whose `urn` carries the URL for resolution. */
const buildUrlSuggestion = (url: string, type: LinkedinUrlType): LinkedinPersonSuggestionItem => ({
	urn: url,
	title: URL_OPTION_TITLE[type],
	subtitle: url,
});

export interface EnrichedProfileSuggestions {
	personSuggestions: LinkedinPersonSuggestionItem[] | undefined;
	setPersonSuggestions: Dispatch<SetStateAction<LinkedinPersonSuggestionItem[] | undefined>>;
	setPersonSearchValue: (value: string) => void;
	isLoadingPeople: boolean;
	organizationSuggestions: LinkedinPersonSuggestionItem[] | undefined;
	setOrganizationSuggestions: Dispatch<SetStateAction<LinkedinPersonSuggestionItem[] | undefined>>;
	setCompanySearchValue: (value: string) => void;
	isLoadingCompanies: boolean;
	resolvePerson: (enrichId: string) => Promise<Suggestion>;
	resolveCompany: (enrichId: string) => Promise<Suggestion>;
	isResolvingPerson: boolean;
	isResolvingCompany: boolean;
}

/**
 * CompanyEnrich-backed person/company autocompletes for the BY_PROFILE source. Suggestion
 * items carry the CompanyEnrich id in `urn` for display/selection only — a selection must be
 * passed through `resolvePerson`/`resolveCompany`, which return the final suggestion whose
 * `urn` is the real LinkedIn identifier required by the post scraper.
 */
export const useEnrichedProfileSuggestions = (): EnrichedProfileSuggestions => {
	const { query, setSearchValue: setPersonSearchValue } = useSearch();
	const { query: companyQuery, setSearchValue: setCompanySearchValue } = useSearch();

	const [personSuggestions, setPersonSuggestions] = useState<LinkedinPersonSuggestionItem[]>();
	const [organizationSuggestions, setOrganizationSuggestions] =
		useState<LinkedinPersonSuggestionItem[]>();

	// `isFetching` (not `isLoading`): search-as-you-type needs the spinner on every keystroke fetch.
	const [fetchPeople, { isFetching: isLoadingPeople }] = useLazyGetEnrichedPersonSuggestionQuery();
	const [fetchCompanies, { isFetching: isLoadingCompanies }] =
		useLazyGetEnrichedCompanySuggestionQuery();

	const [resolvePersonQuery, { isFetching: isResolvingPerson }] =
		useLazyResolveEnrichedPersonSuggestionQuery();
	const [resolveCompanyQuery, { isFetching: isResolvingCompany }] =
		useLazyResolveEnrichedCompanySuggestionQuery();
	const [resolvePersonByUrlQuery, { isFetching: isResolvingPersonByUrl }] =
		useLazyResolveEnrichedPersonByUrlQuery();
	const [resolveCompanyByUrlQuery, { isFetching: isResolvingCompanyByUrl }] =
		useLazyResolveEnrichedCompanyByUrlQuery();

	const toSuggestionItem = (suggestion: EnrichedSuggestion): LinkedinPersonSuggestionItem => ({
		urn: suggestion.id,
		title: suggestion.title,
		subtitle: suggestion.subtitle,
		image: suggestion.image,
	});

	useEffect(() => {
		if (!query) {
			return;
		}

		const classified = classifyLinkedinUrl(query);

		// Any LinkedIn URL bypasses CompanyEnrich search: the right type resolves directly, a
		// wrong-type one (e.g. a company URL in the person field) yields no options.
		if (classified) {
			setPersonSuggestions(
				classified.type === "PERSON" ? [buildUrlSuggestion(classified.url, "PERSON")] : []
			);

			return;
		}

		fetchPeople({ keyword: query }).then((results) => {
			const optionValues = results?.data?.suggestions;

			if (optionValues) {
				setPersonSuggestions(optionValues.map(toSuggestionItem));
			}
		});
	}, [fetchPeople, query]);

	useEffect(() => {
		if (!companyQuery) {
			return;
		}

		const classified = classifyLinkedinUrl(companyQuery);

		if (classified) {
			setOrganizationSuggestions(
				classified.type === "COMPANY" ? [buildUrlSuggestion(classified.url, "COMPANY")] : []
			);

			return;
		}

		fetchCompanies({ keyword: companyQuery }).then((results) => {
			const optionValues = results?.data?.suggestions;

			if (optionValues) {
				setOrganizationSuggestions(optionValues.map(toSuggestionItem));
			}
		});
	}, [fetchCompanies, companyQuery]);

	// A suggestion's `urn` is a CompanyEnrich id for search results and a URL for pasted URLs.
	const resolvePerson = (urnOrUrl: string): Promise<Suggestion> =>
		classifyLinkedinUrl(urnOrUrl)
			? resolvePersonByUrlQuery({ url: urnOrUrl }).unwrap()
			: resolvePersonQuery({ id: urnOrUrl }).unwrap();

	const resolveCompany = (urnOrUrl: string): Promise<Suggestion> =>
		classifyLinkedinUrl(urnOrUrl)
			? resolveCompanyByUrlQuery({ url: urnOrUrl }).unwrap()
			: resolveCompanyQuery({ id: urnOrUrl }).unwrap();

	return {
		personSuggestions,
		setPersonSuggestions,
		setPersonSearchValue,
		isLoadingPeople,
		organizationSuggestions,
		setOrganizationSuggestions,
		setCompanySearchValue,
		isLoadingCompanies,
		resolvePerson,
		resolveCompany,
		isResolvingPerson: isResolvingPerson || isResolvingPersonByUrl,
		isResolvingCompany: isResolvingCompany || isResolvingCompanyByUrl,
	};
};
